import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { CalendarDays, Clock, List as ListIcon } from "lucide-react";
import { Filters } from "./Filters";
import { ListView } from "./ListView";
import { CalendarView } from "./CalendarView";
import { TimelineView } from "./TimelineView";
import { fetchCategories, fetchDates, fetchStatuses } from "../../lib/api";
import { useReveal } from "../../lib/useReveal";

const VIEW_OPTIONS = [
  { id: "calendar", label: "Calendar", Icon: CalendarDays },
  { id: "list", label: "List", Icon: ListIcon },
  { id: "timeline", label: "Timeline", Icon: Clock },
];

const readQueryFromUrl = () => {
  if (typeof window === "undefined") return "";
  try {
    const sp = new URLSearchParams(window.location.search);
    return sp.get("q") || "";
  } catch {
    return "";
  }
};

export const Explorer = ({ onOpenDetail }) => {
  const [view, setView] = useState("list");
  const [q, setQ] = useState(() => readQueryFromUrl());
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [month, setMonth] = useState("");

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [categories, setCategories] = useState([]);
  const [statuses, setStatuses] = useState([]);

  const ref = useReveal();
  const initialQRef = useRef(readQueryFromUrl());
  const location = useLocation();

  // Default to list view if a search term came in via URL — list reads better
  // when results span many months. Calendar remains the default otherwise.
  useEffect(() => {
    setView(initialQRef.current ? "list" : "calendar");
  }, []);

  // Sync the search input whenever the URL's ?q= changes (e.g. when the
  // SearchOverlay navigates to /?q=...#explorer while we're already on /).
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const next = sp.get("q") || "";
    setQ((current) => (current === next ? current : next));
    if (next) setView("list");
  }, [location.search]);

  // Load filter options once
  useEffect(() => {
    Promise.all([fetchCategories(), fetchStatuses()]).then(([c, s]) => {
      setCategories(c);
      setStatuses(s);
    }).catch(() => {});
  }, []);

  // Reload entries on filter change
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    const params = {
      q: q.trim() || undefined,
      category: category || undefined,
      recognition_status: status || undefined,
      month: month ? Number(month) : undefined,
    };
    const handle = setTimeout(() => {
      fetchDates(params)
        .then((data) => {
          if (!alive) return;
          setEntries(data);
        })
        .catch((e) => alive && setError(e?.message || "Could not load the archive."))
        .finally(() => alive && setLoading(false));
    }, q ? 220 : 0);
    return () => { alive = false; clearTimeout(handle); };
  }, [q, category, status, month]);

  const onClear = () => {
    setQ(""); setCategory(""); setStatus(""); setMonth("");
  };

  const resultCount = entries.length;

  const Body = useMemo(() => {
    if (loading) {
      return (
        <div data-testid="loading" aria-busy="true" style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: "1rem",
        }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} style={{ height: 180, background: "hsl(var(--bg-elev))", border: "1px solid hsl(var(--line))", opacity: 0.55 }} />
          ))}
        </div>
      );
    }
    if (error) {
      return <div role="alert" style={{ padding: "1.5rem", border: "1px solid hsl(var(--line))" }}>{error}</div>;
    }
    if (view === "list") return <ListView entries={entries} onOpenDetail={onOpenDetail} />;
    if (view === "timeline") return <TimelineView entries={entries} onOpenDetail={onOpenDetail} />;
    return <CalendarView entries={entries} onOpenDetail={onOpenDetail} />;
  }, [loading, error, view, entries, onOpenDetail]);

  return (
    <section id="explorer" className="section reveal" ref={ref} data-testid="explorer-section">
      <div className="shell">
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: "1.5rem", marginBottom: "2rem" }}>
          <div>
            <span className="section__eyebrow">Calendar Explorer</span>
            <h2 style={{ marginTop: 0 }}>Browse the archive across the year and across centuries.</h2>
          </div>
          <div role="tablist" aria-label="Explorer view" style={{
            display: "inline-flex",
            border: "1px solid hsl(var(--line))",
            background: "hsl(var(--bg-elev))",
            borderRadius: 999,
            padding: 4,
          }}>
            {VIEW_OPTIONS.map(({ id, label, Icon }) => {
              const active = view === id;
              return (
                <button
                  key={id}
                  role="tab"
                  aria-selected={active}
                  data-testid={`view-toggle-${id}`}
                  onClick={() => setView(id)}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "0.55rem 1rem",
                    fontSize: "0.85rem",
                    fontWeight: 500,
                    background: active ? "hsl(var(--accent))" : "transparent",
                    color: active ? "hsl(var(--accent-ink))" : "hsl(var(--ink))",
                    border: "none",
                    cursor: "pointer",
                    borderRadius: 999,
                    transition: "background-color 160ms ease, color 160ms ease",
                  }}
                >
                  <Icon size={14} aria-hidden />
                  {label}
                </button>
              );
            })}
          </div>
        </header>

        <Filters
          q={q} setQ={setQ}
          category={category} setCategory={setCategory}
          status={status} setStatus={setStatus}
          month={month} setMonth={setMonth}
          categories={categories}
          statuses={statuses}
          onClear={onClear}
          resultCount={resultCount}
        />

        <div style={{ marginTop: "2rem" }}>
          {Body}
        </div>
      </div>
    </section>
  );
};
